import { DefaultWorklistASAMs } from "enums/Methods";
import { DeadTotalCountSpinOrder } from "features/WorklistTools/shared/interfaces";
import {
  calculateSpinParamGForce,
  getValueInDetailsString,
} from "features/WorklistTools/shared/WorklistHelpers";
import { ITwelve24TCMethodSettings } from "../../state/initial-state";

/**
 * The subset of a worklist/template row the method settings are encoded in.
 * Both sources are normalized to this shape so the two paths cannot drift apart.
 */
export interface MethodSettingsRow {
  task?: string | null;
  details?: string | null;
  transferVol?: string | null;
}

export interface Parse1224TCMethodSettingsOptions {
  /** Downloaded worklists store times in seconds, templates store them in minutes. */
  incomingTimeUnit: "seconds" | "minutes";
  selectedSystem?: number;
  arraySystemAutomationMethodId?: number;
}

export const parse1224TCMethodSettings = (
  rows: MethodSettingsRow[],
  {
    incomingTimeUnit,
    selectedSystem = 0,
    arraySystemAutomationMethodId = DefaultWorklistASAMs.Twelve24TC,
  }: Parse1224TCMethodSettingsOptions,
): ITwelve24TCMethodSettings => {
  const getRowByTask = (task: string) => rows.find((e) => e?.task === task);
  const getDetails = (task: string) => getRowByTask(task)?.details ?? "";

  // The UI always works in minutes
  const toMinutes = (value: number) =>
    incomingTimeUnit === "seconds" ? Math.round(value / 60) : value;

  const getNullableIntFromDetailsString = (
    detailsString: string,
    task: string,
  ) => {
    const rawValue = getValueInDetailsString(detailsString, task, "");
    return rawValue === "" ? null : parseInt(rawValue, 10);
  };

  const deadTotalPlateRow = getRowByTask("DeadTotalPlate");
  const deadTotalPlateString = deadTotalPlateRow?.details ?? "";
  const incubationString = getDetails("Incubation");
  const pelletResuspensionString = getDetails("PelletResuspension");
  const spinString = getDetails("Spin");
  const dissociationString = getDetails("Dissociation");
  const defaultVSpinString = getDetails("DefaultVSpinSettings");

  const spinParamPercent = parseInt(
    getValueInDetailsString(spinString, "VSpinRPM", "0"),
  );

  // pelletResuspensionRGT 5 (DNA Shield) is written out as reagent position 4
  const getPelletResuspensionRGT = () => {
    const isDNAShield = !!parseInt(
      getValueInDetailsString(pelletResuspensionString, "IsDNAShield", "0"),
    );
    const pelletResuspensionRGT = parseInt(
      getValueInDetailsString(pelletResuspensionString, "ReagentPosition", "0"),
    );

    return isDNAShield ? 5 : pelletResuspensionRGT;
  };

  const prePelletSplitTimer = getNullableIntFromDetailsString(
    pelletResuspensionString,
    "PreSplitTimer",
  );
  const deadTotalCountSpinOrder = getNullableIntFromDetailsString(
    deadTotalPlateString,
    "CountPreSpin",
  ) as DeadTotalCountSpinOrder | null;

  const aliquotVolume = String(deadTotalPlateRow?.transferVol ?? "");

  const methodSettings: ITwelve24TCMethodSettings = {
    selectedSystem,
    arraySystemAutomationMethodId,
    aliquotVolume: aliquotVolume === "" ? 10 : parseInt(aliquotVolume, 10),
    dissociationTime: toMinutes(
      parseInt(
        getValueInDetailsString(incubationString, "IncubationTimeSeconds", "0"),
      ),
    ),
    prePelletSplitTimer:
      prePelletSplitTimer === null ? null : toMinutes(prePelletSplitTimer),
    spinParamGForce: calculateSpinParamGForce(spinParamPercent),
    spinParamPercent: spinParamPercent,
    spinTime: toMinutes(
      parseInt(getValueInDetailsString(spinString, "VSpinTime", "0")),
    ),
    spinAccel: parseInt(getValueInDetailsString(spinString, "VSpinAccel", "0")),
    spinDecel: parseInt(getValueInDetailsString(spinString, "VSpinDecel", "0")),
    dissociationWashRGT: parseInt(
      getValueInDetailsString(dissociationString, "WashReagentPosition", "0"),
    ),
    dissociation: !!parseInt(
      getValueInDetailsString(dissociationString, "Enable", "0"),
    ),
    defaultVSpinSettings: !!parseInt(defaultVSpinString),
    washBeforeDissociation: !!parseInt(
      getValueInDetailsString(dissociationString, "EnableWash", "0"),
    ),
    pelletResuspensionVol: parseInt(
      getValueInDetailsString(pelletResuspensionString, "ReagentVolume", "0"),
    ),
    pelletResuspensionRGT: getPelletResuspensionRGT(),
    deadTotalCountSpinOrder: deadTotalCountSpinOrder,
  };

  return methodSettings;
};
